import { differenceInSeconds } from 'date-fns';
import { MutableRefObject, useCallback, useEffect, useState } from 'react';
import { useIntersection } from 'react-use';

import { datadogRum } from '@datadog/browser-rum';

import {
    GetImageForResourceApiResponse,
    useLazyGetImageForResourceQuery,
} from 'src/store/sageAdminApi';

const DAY_IN_SECONDS = 86_400;

/**
 * A static, stable object reference to use as a default value for the optional imageContainerRef.
 * This prevents useIntersection from endlessly re-rendering when imageContainerRef is not provided.
 */
const STATIC_EMPTY_REF: MutableRefObject<null> = { current: null };

interface CachedImage {
    imageData: string;
    timestamp: number;
}

interface UseImageCacheOptions {
    /**
     * Optional ref to the image container, used to determine if image is visible
     */
    imageContainerRef?: MutableRefObject<HTMLElement | null>;
    /**
     * Number of seconds before image should be refetched
     */
    expiresIn?: number;
    /**
     * Whether to use localStorage instead of sessionStorage, defaults to false
     */
    useLocalStorage?: boolean;
}

interface UseImageCacheResult {
    isLoading: boolean;
    isFetching: boolean;
    isError: boolean;
    refetch: () => Promise<void>;
}

/**
 * Custom hook for caching and managing image resources
 * @param resourceId - The id of the resource to fetch
 * @param imageValueOverride - Optional image value to use instead of fetching
 * @param options - Configuration options for the hook
 * @returns A tuple containing the image data and status object
 */
function useImageCache(
    resourceId?: string,
    imageValueOverride?: string,
    options?: UseImageCacheOptions
): [string | undefined, UseImageCacheResult] {
    const [image, setImage] = useState<CachedImage | undefined>(undefined);

    const {
        imageContainerRef = STATIC_EMPTY_REF,
        expiresIn = DAY_IN_SECONDS,
        useLocalStorage = false,
    } = options || {};

    const imageIntersection = useIntersection(imageContainerRef, {});
    const isVisible = !!imageIntersection?.isIntersecting;

    const [getMediaResource, { isLoading, isFetching, isError }] =
        useLazyGetImageForResourceQuery();

    const updateAndSyncCache = useCallback(
        (resourceId: string, imageData: string) => {
            const value = { imageData, timestamp: Date.now() };
            setImage(value);

            try {
                if (useLocalStorage) {
                    localStorage?.setItem(resourceId, JSON.stringify(value));
                } else {
                    sessionStorage?.setItem(resourceId, JSON.stringify(value));
                }
            } catch (error) {
                datadogRum.addError(error, {
                    message: `Error caching image for resource ${resourceId}`,
                    resourceId,
                    storageType: useLocalStorage ? 'localStorage' : 'sessionStorage',
                });
            }
        },
        [useLocalStorage]
    );

    const fetchAndCacheImage = useCallback(async () => {
        try {
            if (resourceId && !resourceId.startsWith('ri.mock')) {
                const response = await getMediaResource({ resourceId });
                if ('data' in response) {
                    const imageData = response.data as GetImageForResourceApiResponse;
                    updateAndSyncCache(resourceId, imageData);
                }
            }
        } catch (error) {
            datadogRum.addError(error, {
                message: `Error fetching image for resource`,
                resourceId,
            });
        }
    }, [getMediaResource, resourceId, updateAndSyncCache]);

    const getImage = useCallback(() => {
        if (!resourceId) return;

        const cachedImage = useLocalStorage
            ? localStorage?.getItem(resourceId)
            : sessionStorage?.getItem(resourceId);

        if (cachedImage) {
            let parsedImage: CachedImage | undefined;
            try {
                parsedImage = JSON.parse(cachedImage);
            } catch (error) {
                datadogRum.addError(error, {
                    message: `Error parsing cached image for resource`,
                    resourceId,
                    storageType: useLocalStorage ? 'localStorage' : 'sessionStorage',
                });
            }
            const { imageData, timestamp } = parsedImage || {};

            if (
                // If we don't have an image yet and either don't have a ref or the image is visible
                (imageData == null && (imageContainerRef?.current == null || isVisible)) ||
                // Or if we have a timestamp of when the image was last fetched, but it's expired
                (expiresIn && timestamp && differenceInSeconds(Date.now(), timestamp) > expiresIn)
            ) {
                fetchAndCacheImage();
            } else {
                setImage(parsedImage);
            }
        } else if (imageContainerRef?.current == null || isVisible) {
            fetchAndCacheImage();
        }
    }, [expiresIn, fetchAndCacheImage, imageContainerRef, isVisible, resourceId, useLocalStorage]);

    useEffect(() => {
        // If we have an image value override, use that instead of fetching
        if (resourceId && imageValueOverride) {
            updateAndSyncCache(resourceId, imageValueOverride);
        } else if (resourceId) {
            getImage();
        }
    }, [getImage, imageValueOverride, resourceId, updateAndSyncCache]);

    return [image?.imageData, { isLoading, isFetching, isError, refetch: fetchAndCacheImage }];
}

export default useImageCache;
